import type { ReactNode } from "react";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";

/**
 * All figures on this page are example/demo data for illustration only \u2014
 * no telephony, SMS, or AI qualification is wired up yet (that's later
 * steps). See DASHBOARD_DEMO_METRICS below for the one place to edit them.
 */
const DASHBOARD_DEMO_METRICS = [
  { label: "Missed calls", value: "38", hint: "Last 30 days" },
  { label: "AI conversations", value: "31", hint: "Replied to the follow-up text" },
  { label: "Qualified leads", value: "22", hint: "Matched your business rules" },
  { label: "Converted leads", value: "14", hint: "Became paying customers" },
  { label: "Est. revenue recovered", value: "$6,300", hint: "Based on your average job value" },
];

const demoLeadRows = [
  { name: "Maria Alvarez", need: "Water heater quote", status: "Qualified" as const, time: "3m ago" },
  { name: "James Whitfield", need: "Emergency leak", status: "New" as const, time: "12m ago" },
  { name: "Priya Shah", need: "Annual inspection", status: "Qualified" as const, time: "41m ago" },
  { name: "Devon Rios", need: "Estimate for install", status: "Converted" as const, time: "2h ago" },
];

const statusTone: Record<string, "success" | "signal" | "neutral"> = {
  Qualified: "success",
  New: "signal",
  Converted: "success",
};

export function DashboardPreview() {
  return (
    <section className="border-t border-line bg-paper-raised py-20">
      <div className="container-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-3xl text-ink">Every lead, in one place</h2>
          <Badge tone="neutral">Example dashboard \u2014 demo data</Badge>
        </div>
        <p className="mt-3 max-w-prose text-ink-soft">
          No more digging through voicemail and text threads. Every missed call becomes a
          tracked, measurable outcome your team can act on \u2014 shown here with sample data so
          you can see what your dashboard will look like.
        </p>

        <Card className="mt-8 p-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {DASHBOARD_DEMO_METRICS.map((metric) => (
              <div key={metric.label} className="rounded-lg border border-line bg-paper p-4">
                <p className="text-xs font-medium text-ink-soft">{metric.label}</p>
                <p className="mt-1 font-display text-2xl text-ink">{metric.value}</p>
                <p className="mt-1 text-xs text-ink-soft">{metric.hint}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 overflow-hidden rounded-lg border border-line">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-line bg-paper text-ink-soft">
                <tr>
                  <th className="px-5 py-3 font-medium">Customer</th>
                  <th className="px-5 py-3 font-medium">Need</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Received</th>
                </tr>
              </thead>
              <tbody>
                {demoLeadRows.map((row) => (
                  <tr key={row.name} className="border-b border-line last:border-0 bg-paper-raised">
                    <td className="px-5 py-3 font-medium text-ink">{row.name}</td>
                    <td className="px-5 py-3 text-ink-soft">{row.need}</td>
                    <td className="px-5 py-3">
                      <Badge tone={statusTone[row.status]}>{row.status}</Badge>
                    </td>
                    <td className="px-5 py-3 text-ink-soft">{row.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-ink-soft">
            Shown with example data for illustration. Your dashboard fills in with your own
            calls, conversations, and leads.
          </p>
        </Card>
      </div>
    </section>
  );
}

const featureIcons: Record<string, ReactNode> = {
  followUp: (
    <path d="M8 3v3m8-3v3M4.5 9h15M6 5h12a1.5 1.5 0 0 1 1.5 1.5V19A1.5 1.5 0 0 1 18 20.5H6A1.5 1.5 0 0 1 4.5 19V6.5A1.5 1.5 0 0 1 6 5Z" />
  ),
  sms: <path d="M4 5h16v11H8l-4 4V5Z" />,
  ai: <path d="M12 3v3m0 12v3m9-9h-3M6 12H3m14.5-6.5-2 2m-9 9-2 2m13 0-2-2m-9-9-2-2M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z" />,
  rules: <path d="M4 6h16M4 12h10M4 18h6m9-3-3 3 3 3" />,
  dashboard: <path d="M4 5h7v6H4V5Zm9 0h7v14h-7V5ZM4 13h7v6H4v-6Z" />,
  analytics: <path d="M5 20V10m6.5 10V4m6.5 16v-7" />,
  history: <path d="M12 8v4l3 2M4.9 4.9A9 9 0 1 1 3 12" />,
  notifications: <path d="M15 17H5l1.4-2.8A4 4 0 0 0 7 12.3V10a5 5 0 0 1 10 0v2.3a4 4 0 0 0 .6 1.9L19 17h-4Zm-6 0a3 3 0 0 0 6 0" />,
  automation: <path d="M12 5v2m0 10v2m7-9h-2M7 12H5m10.6-5.6-1.4 1.4M8.8 15.2l-1.4 1.4m9.2 0-1.4-1.4M8.8 8.8 7.4 7.4M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z" />,
};

function FeatureIcon({ name }: { name: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-brand-600">
      {featureIcons[name]}
    </svg>
  );
}

const features = [
  { icon: "followUp", title: "Missed-call follow-up", detail: "Every missed call automatically triggers a follow-up \u2014 nothing to remember, nothing manual." },
  { icon: "sms", title: "SMS automation", detail: "A text goes out within seconds, in your business's voice, so the caller hears from you before they call the next name on their list." },
  { icon: "ai", title: "AI lead qualification", detail: "Replies are checked against what actually makes a lead worth your team's time, not just \u201csomeone responded.\u201d" },
  { icon: "rules", title: "Custom business rules", detail: "Set the questions, criteria, and thresholds that define a qualified lead for your business specifically." },
  { icon: "dashboard", title: "Lead dashboard", detail: "Every missed call, conversation, and outcome in one place \u2014 sorted, searchable, and up to date." },
  { icon: "analytics", title: "Analytics", detail: "See missed calls, response times, qualification rates, and conversions over time." },
  { icon: "history", title: "Conversation history", detail: "The full back-and-forth is saved against every lead, so your team has context before they call back." },
  { icon: "notifications", title: "Notifications", detail: "Get alerted the moment a lead is qualified, so follow-up happens while it still matters." },
  { icon: "automation", title: "Automation settings", detail: "Control message timing, tone, escalation rules, and what happens when the AI isn't confident." },
];

export function Features() {
  return (
    <section id="features" className="py-20">
      <div className="container-page">
        <h2 className="font-display text-3xl text-ink">What Callback does</h2>
        <p className="mt-3 max-w-prose text-ink-soft">
          Everything needed to turn a missed call into a handled opportunity \u2014 without
          adding another inbox for your team to check.
        </p>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <Card key={f.title} className="p-6">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-brand-50">
                <FeatureIcon name={f.icon} />
              </div>
              <p className="mt-4 font-medium text-ink">{f.title}</p>
              <p className="mt-2 text-sm text-ink-soft">{f.detail}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

const roiStats = [
  { value: "Seconds", label: "Typical time to first response, not hours" },
  { value: "24/7", label: "Coverage, including nights and closed days" },
  { value: "Fewer", label: "Hours your team spends chasing cold callbacks" },
];

export function ValueProp() {
  return (
    <section className="border-t border-line bg-brand-900 py-20 text-white">
      <div className="container-page">
        <h2 className="font-display text-3xl">Time you get back, opportunities you keep</h2>
        <p className="mt-3 max-w-prose text-brand-100">
          Every business is different, so we won&apos;t hand you a made-up revenue number or a
          guaranteed result. What Callback does is measurable and specific: fewer missed calls
          go unanswered, follow-up happens automatically instead of \u201cwhen someone has time,\u201d
          and your team spends its time on leads that are already qualified.
        </p>
        <div className="mt-10 grid gap-8 sm:grid-cols-3">
          {roiStats.map((s) => (
            <div key={s.label}>
              <p className="font-display text-3xl">{s.value}</p>
              <p className="mt-1 text-sm text-brand-100">{s.label}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-xs text-brand-100/80">
          The dashboard above shows an example of how missed calls, conversations, and
          qualified leads get tracked so you can see the value for yourself, in your own
          numbers, once you&apos;re live.
        </p>
      </div>
    </section>
  );
}
